import Router from 'next/router';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import ReactGA from 'react-ga4';

import { UaEventOptions } from 'react-ga4/types/ga4';
import { LinkedInInsightTag, linkedInTrack } from 'nextjs-linkedin-insight-tag';
import { PirschAnalyticsContext, PirschEvent } from '@/contexts/PirschAnalyticsContext';
import { AuthStateContext } from './AuthContext';
import Script from 'next/script';

export type AnalyticsLogEvent = UaEventOptions & Partial<PirschEvent>;
export type AnalyticsContextType = {
  consent: boolean | null;
  setConsent(consent: boolean): void;
  logEvent(params: AnalyticsLogEvent): void;
};

type AnalyticsContextProps = {
  children: React.ReactNode;
};

export const AnalyticsContext = createContext({} as AnalyticsContextType);

export const AnalyticsProvider = ({ children }: AnalyticsContextProps): any => {
  const [consent, setConsent] = useState<boolean | null>(null);
  const { user } = useContext(AuthStateContext);
  const { logEvent: LogPirschEvent } = useContext(PirschAnalyticsContext);

  // we create a default state to keep track of whether GA
  // has been initialized, if we're tracking a unique user,
  // and to hold all of our trackers
  const [analytics, setAnalytics] = useState({
    isInitialized: false,
    hasUser: false
  });

  // We create a function handle all route changes that occur
  // and track a users movements across pages in our app
  const handleRouteChange = (url) => {
    ReactGA.send({ hitType: 'pageview', page: url });
  };

  // Look for previous consent on localstorage.
  useEffect(() => {
    const storedConsent = localStorage.getItem('cookie_consent');
    if (storedConsent === 'true') {
      setConsent(true);
      // loadAnalytics();
    } else if (storedConsent === 'false') {
      setConsent(false);
    }
  }, []);

  // We'll initialize GA when the component mounts
  useEffect(() => {
    if (typeof window !== 'undefined' && window.gtag && consent) {
      window.gtag('js', new Date());
      window.gtag('config', process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS);

      // We'll set up a listener for route changes
      Router.events.on('routeChangeComplete', handleRouteChange);
    }
  }, [consent]);

  // We'll initialize GA when the user logs in
  useEffect(() => {
    if (typeof window !== 'undefined' && window.gtag && user) {
      window.gtag('set', { user_id: user.id });
    }
  }, [user]);

  // We'll define our logEvent function before useEffect
  const logEvent = (params: AnalyticsLogEvent) => {
    // Return if the user did not consent to cookies.
    if (!consent) return;

    if (analytics.isInitialized) {
      ReactGA.event({ ...params });

      switch (params.action) {
        case 'signed_up':
          linkedInTrack(20497057);
          break;
        default:
          break;
      }

      const pirschParams: PirschEvent = {
        name: params.name || (!params.action ? params.category : `${params.category}: ${params.action}`),
        meta: { ...params.meta }
      };

      if (!params.meta && params.label) {
        pirschParams.meta = { ...(pirschParams.meta || {}), Label: params.label };
      }

      if (!params.meta && params.value) {
        pirschParams.meta = { ...(pirschParams.meta || {}), Value: params.value };
      }

      try {
        LogPirschEvent(pirschParams)
          .then()
          .catch((err) => console.error(err));
      } catch (e) {
        console.error(e);
      }
    }
  };

  // We only want to initialize GA on the client side
  // This will fail if you're trying to initialize server side
  // useEffect will help us handle this case as it only runs
  // client side
  useEffect(() => {
    const { isInitialized, hasUser } = analytics;

    // initialize GA with our tracking id
    if (!isInitialized && consent) {
      ReactGA.initialize([
        {
          trackingId: process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS || '',
          gaOptions: user?.id ? { userId: user?.id } : {}
        },
        {
          trackingId: process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER || '',
          gaOptions: user?.id ? { userId: user?.id } : {}
        }
      ]);

      // Handle all route changes
      Router.events.on('routeChangeComplete', handleRouteChange);

      setAnalytics((prev) => ({
        ...prev,
        isInitialized: true,
        hasUser: Boolean(user?.id)
      }));

      // in case we don't have the user initially,
      // we handle setting a user in our tracker
    } else if (isInitialized && !hasUser && user?.id) {
      ReactGA.set({ userId: user?.id });

      setAnalytics((prev) => ({
        ...prev,
        hasUser: Boolean(user?.id)
      }));
    }

    // clean up
    return () => {
      Router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [analytics, user?.id]);

  const dispatchContextValue = useMemo(() => ({ logEvent, consent, setConsent }), [logEvent, consent, setConsent]);

  return (
    <AnalyticsContext.Provider value={dispatchContextValue}>
      {children}

      {/* Google Scripts (loaded only if user consents) */}
      {consent && (
        <>
          {/* Google Scripts */}
          <Script
            // strategy='lazyOnload'
            src={`https://www.googletagmanager.com/gtag/js?id=${process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER}`}
          />
          <Script id='ga-analytics'>
            {`
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
    
                gtag('config', '${process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER}', { anonymize_ip: true }); // Google Ads
              `}
          </Script>

          {/* LinkedIn Ads */}
          <LinkedInInsightTag />

          {/* ZoomInfo Script (only on public pages) */}
          <Script id='zoominfo-script'>
            {`
              window[(function(_UEQ, _sx) {
                var _4E = '';
                for (var _nP = 0; _nP < _UEQ.length; _nP++) {
                    var _pb = _UEQ[_nP].charCodeAt();
                    _pb != _nP;
                    _pb -= _sx;
                    _pb += 61;
                    _pb %= 94;
                    _pb += 33;
                    _4E == _4E;
                    _sx > 6;
                    _4E += String.fromCharCode(_pb)
                }
                return _4E
            })(atob('b15lKSYhengrYHow'), 21)] = 'df7a0cfeb81683796894';
            var zi = document.createElement('script');
            (zi.type = 'text/javascript'), (zi.async = true), (zi.src = (function(_A4B, _hU) {
                var _5y = '';
                for (var _np = 0; _np < _A4B.length; _np++) {
                    _is != _np;
                    var _is = _A4B[_np].charCodeAt();
                    _is -= _hU;
                    _is += 61;
                    _hU > 3;
                    _is %= 94;
                    _is += 33;
                    _5y == _5y;
                    _5y += String.fromCharCode(_is)
                }
                return _5y
            })(atob('Ljo6NjleU1MwOVJAL1E5KTgvNjo5Uik1M1NAL1E6Jy1SMDk='), 36)
            ), document.readyState === 'complete' ? document.body.appendChild(zi) : window.addEventListener('load', function() {
                document.body.appendChild(zi)
            });
            `}
          </Script>
        </>
      )}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => {
  const c = useContext(AnalyticsContext);
  if (!c) throw new Error('Cannot use useAnalytics when not under the AnalyticsProvider');
  return c;
};
